/**
 * Registers Native Notification factories.
 * Factories return {@see NativeNotificationParams} based on {@see NotificationData}.
 *
 * Lookup buildNotificationTag() to know how factory tags are built.
 */
(function () {
    var FACTORY_PREFIX = 'notificationFactory:';
    var TY_MODULE = angular.module('tradersyard');
    var DEFAULT_ICON = '/storage/photo/mock/noimage1x1-256x256.png';

    // TODO: Remove sent.message notification from here and hook it up for message:create socket event
    // registerFactory('sent.message', factoryPrivateMessage);
    registerFactory('sent.request', factoryFriendRequest);
    registerFactory('sent.invite', factoryInvite);
    registerFactory('posted.post', factoryPost);
    registerFactory('liked.comment', factoryLikedComment);

    TY_MODULE.run(registerMessageNotification);
    TY_MODULE.run(registerCallNotification);
    TY_MODULE.run(registerCallInviteNotification);

    // ---

    registerMessageNotification.$inject = ['WebSocket', 'NativeNotificationService', '$rootScope'];
    registerCallNotification.$inject = ['WebSocket', 'NativeNotificationService', '$rootScope', '$window'];
    registerCallInviteNotification.$inject = ['WebSocket', 'NativeNotificationService', '$rootScope', '$window'];

    function registerMessageNotification(WebSocket, NativeNotificationService, $rootScope) {
        WebSocket.on('message:create', function (event) {
            var place = event.node.place;
            var owner = event.node.owner;

            if (place.id < 1) {
                // ignore public chat
                return;
            }

            if (Number(owner && owner.id) === Number($rootScope.me && $rootScope.me.id)) {
                // No need to notify us about our own messages
                return;
            }

            /**
             * For certain event-type messages we won't get any notification
             * @type {NativeNotificationParams|undefined}
             */
            var notificationParams = factoryMessage(event);

            if (notificationParams) {
                NativeNotificationService.showNotification(notificationParams);
            }
        });
    }

    function registerCallNotification(WebSocket, NativeNotificationService, $rootScope, $window) {
        WebSocket.on('meeting:call:request', function (event) {
            if (Number(event.user.id) === Number($rootScope.me.id)) {
                return;
            }

            var title = event.node.place.location.name + ' is calling you';

            var notificationParams = {
                title: title,
                options: {
                    tag: ['call', event.node.id].join('-'),
                    body: '',
                    renotify: true,
                    icon: DEFAULT_ICON,
                },
                events: {
                    click: function (e) {
                        $window.focus();
                    },
                },
            };

            NativeNotificationService.showNotification(notificationParams);
        });
    }

    function registerCallInviteNotification(WebSocket, NativeNotificationService, $rootScope, $window) {
        WebSocket.on('me:meeting:invite', function (event) {
            if (Number(event.user.id) !== Number($rootScope.me.id)) {
                return;
            }

            var title = event.node.inviterName + ' invites you to a group call';

            var notificationParams = {
                title: title,
                options: {
                    tag: ['call', event.guid].join('-'),
                    body: '',
                    renotify: true,
                    icon: DEFAULT_ICON,
                },
                events: {
                    click: function (e) {
                        $window.focus();
                    },
                },
            };

            NativeNotificationService.showNotification(notificationParams);
        });
    }

    function registerFactory(factoryTag, factoryFunc) {
        TY_MODULE.factory(FACTORY_PREFIX + factoryTag, function () {
            return factoryFunc;
        });
    }

    // region Notification factories

    /**
     * @param {Object} event WebSocket message:create event
     * @returns {NativeNotificationParams|undefined}
     */
    function factoryMessage(event) {
        var message = event.node.content;
        var sender = event.node.owner;
        var place = event.node.place;

        /**
         * Sometimes we get some chat events instead of text messages.
         * e.g. {content: {type: "stream.video.start"}}
         *
         * We want to show nice messages
         */
        var eventMessages = {
            'stream.video.start': sender.name + ' enabled web-cam',
            'stream.video.stop': sender.name + ' disabled web-cam',

            'stream.audio.start': sender.name + ' is now speaking',
            'stream.audio.stop': sender.name + ' is now muted',

            'stream.screen.start': sender.name + ' is now screen sharing',
            'stream.screen.stop': sender.name + ' is no longer screen sharing',
        };

        // chat text messages are handled by chat-notification-handler
        // we only handle special event messages here
        if (!message.hasOwnProperty('type')) {
            return false;
        }

        // We received an event, and we DO have a message defined
        const title = eventMessages[message.type];

        if (!title) {
            return false;
        }

        return {
            title: title,
            options: {
                tag: ['message', sender.id].join('-'), // use tag to prevent showing multiple popups from someone who sends many messages fast
                renotify: true,
                icon: DEFAULT_ICON,
            },
            events: {
                click: onClickOpenUrl(['meeting', place.id]),
            },
        };
    }

    /**
     * @param {NotificationData} notificationData
     * @returns {NativeNotificationParams}
     */
    function factoryFriendRequest(notificationData) {
        var invitation = notificationData.object.location;
        var actor = invitation.owner;

        return {
            title: actor.name + ' sent you a friend request.',
            options: {
                tag: ['friend-request', actor.id].join('-'),
                icon: DEFAULT_ICON,
            },
            events: {
                click: onClickOpenUrl([actor.id]),
            },
        };
    }

    /**
     * @param {NotificationData} notificationData
     * @returns {NativeNotificationParams}
     */
    function factoryInvite(notificationData) {
        var placeType = getNotificationPlaceType(notificationData);
        var place = getNotificationPlace(notificationData);
        var actor = getNotificationActor(notificationData);
        var joinWhat = getNotificationPlaceTypeLabel(notificationData);

        var urlSegment = {
            group: 'group',
            event: 'event',
            webinar: 'webinar',
        }[placeType];

        return {
            title: actor.name + ' invited you to join ' + joinWhat,
            options: {
                tag: ['invite', urlSegment, place.id].join('-'),
                icon: DEFAULT_ICON,
                body: 'Click here to see ' + place.name,
            },
            events: {
                click: onClickOpenUrl([urlSegment, place.id]),
            },
        };
    }

    function factoryPost(notificationData) {
        var actor = getNotificationActor(notificationData);
        var post = notificationData.object.location;

        return {
            title: actor.name + ' published a new post',
            options: {
                tag: ['post', post.id].join('-'),
                icon: DEFAULT_ICON,
                body: 'Click here to see it.',
            },
            events: {
                click: onClickOpenUrl(['post', post.id]),
            },
        };
    }

    function factoryLikedComment(notificationData) {
        var actor = getNotificationActor(notificationData);
        var post = notificationData.object.location.place;

        return {
            title: actor.name + ' liked your comment.',
            options: {
                tag: ['comment-like', post.id].join('-'),
                icon: DEFAULT_ICON,
                body: 'Click here to see the post.',
            },
            events: {
                click: onClickOpenUrl(['post', post.id]),
            },
        };
    }

    // endregion

    // region Helpers

    /**
     * @param {NotificationData} notificationData
     * @returns {NotificationUser}
     */
    function getNotificationActor(notificationData) {
        return notificationData.subjects[0].location;
    }

    /**
     * @param {NotificationData} notificationData
     * @returns {object}
     */
    function getNotificationPlace(notificationData) {
        return notificationData.object.location.place.location;
    }

    function getNotificationPlaceTypeLabel(notificationData) {
        return {
            group: 'an Office',
            event: 'an Event',
            webinar: 'a Webinar',
        }[getNotificationPlaceType(notificationData)];
    }

    /**
     * @param {NotificationData} notificationData
     * @returns {string}
     */
    function getNotificationPlaceType(notificationData) {
        return notificationData.object.location.place.name;
    }

    function onClickOpenUrl(pathSegments, target) {
        var url = [window.location.origin].concat(pathSegments).join('/');
        return function (e) {
            window.open(url, target || 'notification');
        };
    }

    // endregion
})();
